import {AutoComplete, Errors, RichText, TextInput, WithForm, WithImageInput} from '@startlibs/form';
import {Button, Icon, Loading, SplitColumnsContainer} from '@startlibs/components'
import {getColor, ifUndefined} from '@startlibs/utils';
import {useRefState, useToggle} from '@startlibs/core'
import React, {useRef, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import _ from 'lodash/fp'
import { BoxWrapperFooter} from '../../components/BoxPageLayout';
import {ImageInputContainer, ProfilePage, ThumbnailContainer} from './components/ExpertProfileLayout';
import {
  buildValidation,
  intlRequired,
} from '../../utils/validation';
import {getJwt} from '../../hooks/useJwt'
import {jwtGetFetcher, jwtPostFetcher} from '../../utils/authFetch'
import {IfLocale, useIsLocale} from '../../hocs/IfLocale'
import { getReportResource, isRichText, RTF_TOOLBAR_OPTIONS_STARTLIBS, safeMultipleNewLineToBr, safeMultipleNewLineToBrOnly } from '../../utils/utils';
import { REVIEWER_TYPE_EXPERT } from '../../enums/UserRole';
import { ConfirmDialog, useConfirmDialog } from '../../hooks/useConfirmDialog';
import { AssignmentStatusBox } from '../steps/expertReview/ExpertReviewList';
import { Link } from '@startlibs/components';
import { darken } from 'polished';
import styled from 'styled-components';

const BlueLink = styled(Link)`
  color: ${getColor('main')};
  cursor: pointer;
  font-weight: bold;
  cursor: pointer;
  outline: 0;
  outline: none;
  :hover {
    color: ${props => darken(0.1, getColor("main")(props))};
    ${props => props.notClickable && 'color: #7d7d7d; cursor: default;'}
  }
  text-decoration: none;
`

// Specialties autocomplete:
let fetchedSuggestions = null

const fetchSuggestions = (s) => {
  const fetch = fetchedSuggestions || jwtGetFetcher(getJwt())("/api/specializations").then(_.map(({label}) => label))
  fetchedSuggestions = fetch
  return fetch.then(specializations =>
    specializations.filter(t => t.toLowerCase().indexOf(s) >= 0)
  ).then(l => l.indexOf(s) >= 0 ? l : l.concat([{newValue: s}]))
}

const getLabel = (intl) => (v) =>
  _.isString(v.newValue)
    ? intl.formatMessage({
      defaultMessage: `Create option "{newOption}"`,
      description: "EditExpert create new specialization"
    },{newOption:v.newValue})
    : v
const transform = (l) => l ? l.map(v => (v && v.newValue) || v) : l 
// const transform = (l) => console.log('transform', l) || l.map(v => (v && v.newValue) || v)
/************************/

// Image upload
const uploadFetcher = (caseExpertId) => (file) => {
  const formData = new FormData()
  formData.append('file', file)
  return jwtPostFetcher(getJwt())(`/api/expert/report/${caseExpertId}/picture`, formData)
}

/*****************/


const EMPTY_EXPERT = {info: {specialization: []}}
const updateResumeToRTF = (expert) => _.update('expertReviewerInfo.resume', (resume) => resume && resume.includes("\n") ? isRichText(resume) ? safeMultipleNewLineToBrOnly(resume) : safeMultipleNewLineToBr(resume) : resume,expert)
const addConfirmEmail = (expert) => _.update("picture",(image) => !!image, updateResumeToRTF(expert))

export const EditExpertReport = ({expert, id, closeDialog, caseRequest, setCaseRequest,  caseExpertId, formRef }) => {

  const originalPicture = expert ? expert.picture : null
  const [isSubmitting, setIsSubmitting] = useState(false)
  const action = (values) => jwtPostFetcher(getJwt())(`/api/reviewer/share/reportExpert/${caseExpertId}`, values, {method: 'PUT'})
  const intl = useIntl()
  // const formRef = useRef()
  const fileToUpload = useRefState()
  const uploadingFile = useToggle()
  const errorRef = useRef()
  
  const checkImageAndScroll = (file) => {
    if (file.size > 4194304/4) {
      setTimeout(() => errorRef.current.scrollIntoView({block: 'center', behavior: 'smooth'}), 50)
      return [intl.formatMessage({
        defaultMessage:'Your file needs to be smaller than 1mb.',
        description:"EditExpert image upload size error"
      })]
    }
    if (['image/gif', 'image/png', 'image/jpeg', 'image/jpg'].indexOf(file.type.toLowerCase()) < 0) {
      setTimeout(() => errorRef.current.scrollIntoView({block: 'center', behavior: 'smooth'}), 50)
      return [intl.formatMessage({
        defaultMessage: 'Invalid file format. Please select a .JPG or .PNG file.',
        description:"EditExpert image upload format error"
      })]
    }
  }

  const isNotLegal = useIsLocale({not:true,contains:"LEGAL"})

  const preValidation = buildValidation({
      firstName: intlRequired(intl),
      lastName: intlRequired(intl),
      'expertReviewerInfo.specialization': [v => (!v || !v.length) && isNotLegal && intlRequired(intl)()],
    }
  )


  const setFile = (file) => {
    fileToUpload.set(file)
    const image = URL.createObjectURL(file)
    formRef.current.setValue("picture",image)
    return Promise.resolve({image})
  }

  const onSubmit = (values) => {
    setIsSubmitting(true)
    if (fileToUpload.get()) {
      uploadingFile.open()
      uploadFetcher(caseExpertId)(fileToUpload.get()).then((response) => {
        const valuesWithImage = {...values, picture: response.path}
        action(valuesWithImage)
          .then((_expert) => {
            // Must update the case request with the new values
            const updatedReportExpertReviews = caseRequest.reportExpertReviews.map((review) => {
              if (review.expertId === id) {
                const originalValues = review.reportExpertEdit;

                const updatedEdit = {
                  ...originalValues,
                  ...valuesWithImage,
                  name: `${values.firstName} ${values.lastName}`,
                  type: values.type || originalValues?.type || "EXPERT",
                  specialization: values.specialization || originalValues?.specialization || [],
                  resume: values.resume || "",
                };

                return {
                  expertId: review.expertId,
                  hide: review.hide,
                  reviewerType: review.reviewerType,
                  reportExpertEdit: updatedEdit,
                  caseExpertId: review.caseExpertId
                };
              }

              // If it's not the expert, keep the same
              return review;
            });

            const newCaseRequest = {
              ...caseRequest,
              reportExpertReviews: updatedReportExpertReviews
            };
            setCaseRequest(newCaseRequest)
            setIsSubmitting(false)
            closeDialog()
            // return expert
          })
          .catch((e) => {
            console.log(e)
          })
          .finally(() => {
            uploadingFile.close()
          })
      })
    }else{
      const valuesWithImage = {...values, picture: originalPicture}
      action(valuesWithImage)
       .then((_expert) => {
            // Must update the case request with the new values
            const updatedReportExpertReviews = caseRequest.reportExpertReviews.map((review) => {
              if (review.expertId === id) {
                const originalValues = review.reportExpertEdit;

                const updatedEdit = {
                  ...originalValues,
                  ...valuesWithImage,
                  name: `${values.firstName} ${values.lastName}`,
                  type: values.type || originalValues?.type || "EXPERT",
                  specialization: values.specialization || originalValues?.specialization || [],
                  resume: values.resume || "",
                };

                return {
                  expertId: review.expertId,
                  hide: review.hide,
                  reviewerType: review.reviewerType,
                  reportExpertEdit: updatedEdit,
                  caseExpertId: review.caseExpertId
                };
              }

              // If it's not the expert, keep the same
              return review;
            });

            const newCaseRequest = {
              ...caseRequest,
              reportExpertReviews: updatedReportExpertReviews
            };
            setCaseRequest(newCaseRequest)
            setIsSubmitting(false)
            closeDialog()
            // return expert
        })
        .catch((e) => {
          console.log(e)
          setIsSubmitting(false)
          formRef.current.setError("","It was not possible to update this expert")
        })
          
    }
  }

  const pictureId = ifUndefined(expert && expert.id,id)


  const onFailure = (e,[resp,{status}],form) => {
    
    console.log(e,[resp,{status}],form)
    return form.setError("It was not possible to update this expert")
  }
  

  const confirmDiscardChanges = useConfirmDialog(<ConfirmDialog
        css={`max-width: 50rem;`}
        title={'Restore from expert profile'}
        action={() => {
          jwtPostFetcher(getJwt())(`/api/reviewer/share/reportExpert/${caseExpertId}`, {}, {method: 'DELETE'})
            .then((_res) => {
                const updatedReportExpertReviews = caseRequest.reportExpertReviews.map((review) => {
                if (review.expertId === id) {
                  return {
                    expertId: review.expertId,
                    hide: review.hide,
                    reviewerType: review.reviewerType,
                    reportExpertEdit: null,
                    caseExpertId: review.caseExpertId
                  };
                }

                // Otherwise, return the review as-is
                return review;
              });

              const newCaseRequest = {
                ...caseRequest,
                reportExpertReviews: updatedReportExpertReviews
              };
              setCaseRequest(newCaseRequest)
              closeDialog()
            })
            .catch((e) => console.log(e))        
        }}
        confirm={<Button highlight>Restore</Button>}
      >
        <p>Changes made to this expert’s profile on this report will be removed, and the expert’s original profile will be restored.</p>
        <p>Do you want to proceed?</p>
    </ConfirmDialog>)

  return <>
    <WithForm
      alwaysSave
      onFailure={onFailure}
      ref={formRef} 
      preValidation={preValidation} 
      action={onSubmit}
      transform={(v) => {
        return _.flow(
          _.update("resume", () => v.expertReviewerInfo.resume),
          _.update("info.resume", () => v.expertReviewerInfo.resume),
          // Clean specialization: remove '', null, undefined, spaces-only
          _.update("specialization", () =>
            _.filter(s => Boolean(s && String(s).trim()))(v.expertReviewerInfo.specialization)
          ),
          _.update("info.specialization", () =>
            _.filter(s => Boolean(s && String(s).trim()))(v.expertReviewerInfo.specialization)
          )          
        )(v);
      }}
      values={addConfirmEmail(expert) || EMPTY_EXPERT}
    >{form =>
      <ProfilePage>
          <div className="profile-content">
            <div className="fields-wrapper">
              <AssignmentStatusBox reviewed={false} style={{padding: '0.75rem', marginBottom: '1rem'}}>
                <div className="waitingBoxContent" style={{padding: '0rem', margin: '0rem'}}>
                  <p>
                    Changes you make within the expert report will only update the bio within this individual report.
                    {expert?.expertReviewerInfo?.type === REVIEWER_TYPE_EXPERT && <>To permanently edit the expert’s bio <BlueLink to={"/admin/experts/edit/"+expert.id} state={{prevLocation:window.location.pathname,nextLocation:window.location.pathname}}>click here.</BlueLink>
                    </>}
                  </p>
                </div>
              </AssignmentStatusBox>
              <SplitColumnsContainer viewportMinWidth={850}>
                <TextInput
                  readOnly={form.isLoading}
                  autoFocus
                  label={intl.formatMessage({
                    defaultMessage:"First name:",
                    description:"EditExpert first name input label"
                  })}
                  path="firstName"
                  placeholder={intl.formatMessage({
                    defaultMessage: "John",
                    description: "EditExpert first name input placeholder"
                  })}
                  mandatory
                />
                <TextInput
                  readOnly={form.isLoading}
                  label={intl.formatMessage({
                    defaultMessage: "Last name:",
                    description: "EditExpert last name input label"
                  })}
                  path="lastName"
                  placeholder={intl.formatMessage({
                    defaultMessage: "Doe",
                    description: "EditExpert last name input placeholder"
                  })}
                  mandatory
                />
              </SplitColumnsContainer>
              <IfLocale not contains="LEGAL"><SplitColumnsContainer
                viewportMinWidth={850}
                className="split-wrapper"
              >
                <AutoComplete
                  mandatory
                  readOnly={form.isLoading}
                  label={intl.formatMessage({
                    defaultMessage: "Specialization(s):",
                    description: "EditExpert specialization input label"
                  })}
                  confirmIfValueKeys={['Tab']}
                  path="expertReviewerInfo.specialization"
                  fetchSuggestions={fetchSuggestions}
                  getLabel={getLabel(intl)}
                  transform={transform}
                  bellowDescText={intl.formatMessage({
                    defaultMessage: "Use comma, enter or tab to add multiple.",
                    description: "EditExpert specialization input description"
                  })}
                  className="specializations-input"
                />
              </SplitColumnsContainer>
              </IfLocale>
              {/* <RichText
                path="info.resume"
                label={<FormattedMessage defaultMessage="Resume:" description="expert resume label"/>}
                formatTools={RTF_TOOLBAR_OPTIONS_STARTLIBS}
                autoHideToolbar
                disabled={form.isLoading}
              /> */}
            </div>
            <div className="image-wrapper">
              <WithImageInput preValidation={checkImageAndScroll} uploadFetcher={setFile}
                              path={["picture"]}>{(input, {image, isLoading}) => {
                              return <ImageInputContainer hasImage={image} 
                  image={image===true 
                    ? expert?.reportExpertEdited && expert.picture 
                      ? getReportResource(expert.picture,'')
                      : `/api/${expert.expertReviewerInfo.type === REVIEWER_TYPE_EXPERT ? `expert` : `admin`}/${pictureId}/picture?t=${getJwt()}&no-cache=${expert.picture}` 
                    : image
                  }>
                  {!(uploadingFile.isOpen || form.isLoading || isLoading) && input}
                  <ThumbnailContainer css={image === true 
                      ? expert?.reportExpertEdited && expert.picture 
                        ? `background-image:url('${getReportResource(expert.picture,'')}');`
                        : `background-image:url('/api/${expert.expertReviewerInfo.type === REVIEWER_TYPE_EXPERT ? `expert` : `admin`}/${pictureId}/picture?t=${getJwt()}&no-cache=${expert.picture}');` 
                      : (image && `background-image:url('${image}')`)}>
                    {!image && <Icon icon="physician"/>}
                    {(uploadingFile.isOpen || isLoading) && <Loading white absolute/>}
                  </ThumbnailContainer>
                  {!image ?
                    <Button small disabled={uploadingFile.isOpen || form.isLoading || isLoading}><FormattedMessage
                      defaultMessage="Add picture"
                      description="EditExpert add picture button"
                    /></Button>
                    : <Button small highlight disabled={uploadingFile.isOpen || form.isLoading || isLoading}>
                      <FormattedMessage defaultMessage="Change image" description="EditExpert change image button"/>
                    </Button>
                  }
                </ImageInputContainer>}
              }</WithImageInput>
            </div>
          </div>
           <RichText
              path="expertReviewerInfo.resume"
              label={<FormattedMessage defaultMessage="Resume:" description="expert resume label"/>}
              formatTools={RTF_TOOLBAR_OPTIONS_STARTLIBS}
              autoHideToolbar
              disabled={form.isLoading}
            />
          <div ref={errorRef}>
            <Errors/>
          </div>
          <BoxWrapperFooter>
            {
              <>
                <div className="right-block">
                  {expert?.reportExpertEdited && 
                    <Button onClick={() => {
                      confirmDiscardChanges()
                    }}>
                      Restore from profile
                    </Button>
                  }
                  <Button onClick={() => form.confirm("discard-expert-report-edit").then(() => closeDialog())}>
                    <FormattedMessage defaultMessage="Cancel" description="Cancel button"/>
                  </Button>
                  <Button isLoading={isSubmitting} highlight type="submit">
                    <FormattedMessage defaultMessage="Update" description="Update button"/>
                  </Button>
                </div>
              </>
            }
          </BoxWrapperFooter>
      </ProfilePage>
    }
    </WithForm>
  </>
}
